// Tools API Service
// Proxied through /tools-api.php

const API_URL = '/tools-api.php';

const safeJson = async (res) => {
  try { return await res.json(); } catch { return null; }
};

// ── Public endpoints ───────────────────────────────────────────────────

export const fetchMachines = async () => {
  const res = await fetch(`${API_URL}?action=get-machines`);
  if (!res.ok) throw new Error('Failed to fetch machines');
  const data = await safeJson(res);
  if (!data) throw new Error('Failed to fetch machines');
  return data;
};

export const fetchAllTools = async () => {
  const res = await fetch(`${API_URL}?action=get-all-tools`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await safeJson(res);
  if (!Array.isArray(data)) throw new Error('Invalid response');
  return data;
};

export const fetchVendingMachine = async (id) => {
  const res = await fetch(`${API_URL}?action=get-machine&id=${encodeURIComponent(id)}`);
  if (!res.ok) return null;
  return (await safeJson(res)) ?? null;
};

export const fetchTool = async (machineId, toolId) => {
  const res = await fetch(`${API_URL}?action=get-tool&machineId=${encodeURIComponent(machineId)}&toolId=${encodeURIComponent(toolId)}`);
  if (!res.ok) return null;
  return (await safeJson(res)) ?? null;
};

/**
 * Verify a 6-digit pickup code for a vending machine.
 * Returns { toolName, slot, customerPhone } on success.
 */
export const verifyPickupCode = async (code, vendingMachineId) => {
  const res = await fetch(`${API_URL}?action=verify-pickup-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, vendingMachineId }),
  });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(data?.error || 'Помилка перевірки коду');
  return data;
};

/**
 * Return tools by the phone number of the rental.
 * Returns { returned: [{toolName, toolSlot}], identifier }
 */
export const returnByIdentity = async ({ identifier, vendingMachineId, toolId }) => {
  const res = await fetch(`${API_URL}?action=return-by-identity`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, vendingMachineId, ...(toolId && { toolId }) }),
  });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(data?.error || 'Failed to process return');
  return data;
};

// ── Admin helpers ──────────────────────────────────────────────────────

const adminFetch = async (action, { method = 'GET', body, password, params = '' } = {}) => {
  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${password}`,
    },
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${API_URL}?action=${action}${params}`, opts);
  const data = await safeJson(res);
  if (!res.ok) throw new Error(data?.error || 'Request failed');
  return data;
};

// ── Admin auth ─────────────────────────────────────────────────────────

export const adminLogin = async (password) => {
  const res = await fetch(`${API_URL}?action=admin-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(data?.error || 'Login failed');
  return data;
};

// ── Admin machines ─────────────────────────────────────────────────────

export const adminListMachines = (password) =>
  adminFetch('admin-list-machines', { password });

export const adminCreateMachine = (password, machineData) =>
  adminFetch('admin-create-machine', { method: 'POST', password, body: machineData });

export const adminUpdateMachine = (password, machineData) =>
  adminFetch('admin-update-machine', { method: 'POST', password, body: machineData });

export const adminDeleteMachine = (password, id) =>
  adminFetch('admin-delete-machine', { method: 'POST', password, body: { id } });

// ── Admin tools ────────────────────────────────────────────────────────

export const adminListTools = (password, machineId) =>
  adminFetch('admin-list-tools', { password, params: `&machineId=${encodeURIComponent(machineId)}` });

export const adminCreateTool = (password, toolData) =>
  adminFetch('admin-create-tool', { method: 'POST', password, body: toolData });

export const adminUpdateTool = (password, toolData) =>
  adminFetch('admin-update-tool', { method: 'POST', password, body: toolData });

export const adminDeleteTool = (password, id) =>
  adminFetch('admin-delete-tool', { method: 'POST', password, body: { id } });

export const adminResetToolRoi = (password, id) =>
  adminFetch('admin-reset-tool-roi', { method: 'POST', password, body: { id } });

export const adminReorderTools = (password, items) =>
  adminFetch('admin-reorder-tools', { method: 'POST', password, body: { items } });

// ── Admin orders ──────────────────────────────────────────────────────

export const adminListOrders = (password) =>
  adminFetch('admin-list-orders', { password });

export const adminUpdateOrder = (password, id, status) =>
  adminFetch('admin-update-order', { method: 'POST', password, body: { id, status } });
